import { Box, Button, Group, LoadingOverlay, Modal, Text } from '@mantine/core'
import { FormattedMessage, useIntl } from 'react-intl'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import {
  useDeleteApiKey,
  useInvalidateWorkspaceApiKeys
} from '@/queries/workspaceQueries'
import { showToast } from '@/theme/notifications'
import { WorkspaceApiKey } from '@/types/workspace'

type DeleteApiKeyModalProps = {
  apiKey: WorkspaceApiKey
  opened: boolean
  onClose: () => void
}

export const DeleteApiKeyModal = ({
  apiKey,
  opened,
  onClose
}: DeleteApiKeyModalProps) => {
  const intl = useIntl()
  const { mutateAsync: deleteApiKey, isPending } = useDeleteApiKey()
  const { invalidateWorkspaceApiKeys } = useInvalidateWorkspaceApiKeys()

  const handleDelete = async () => {
    try {
      await deleteApiKey(apiKey.id)
      void invalidateWorkspaceApiKeys()
      onClose()
    } catch (err) {
      const fallback = intl.formatMessage({
        id: 'workspace.apiKeys.delete.error'
      })
      showToast(extractErrorMessage(err, fallback), 'error')
    }
  }

  return (
    <Modal
      size="lg"
      title={<FormattedMessage id="workspace.apiKeys.delete.title" />}
      opened={opened}
      onClose={onClose}
    >
      <Box pos="relative">
        <LoadingOverlay visible={isPending} />

        <Text size="sm" mb="xl">
          <FormattedMessage id="workspace.apiKeys.delete.confirmation" />
        </Text>

        <Group justify="end">
          <Button miw={120} variant="default" onClick={onClose}>
            <FormattedMessage id="cancel" />
          </Button>

          <Button miw={120} color="red" onClick={() => void handleDelete()}>
            <FormattedMessage id="delete" />
          </Button>
        </Group>
      </Box>
    </Modal>
  )
}
